import React, { useState, useEffect } from 'react';
import { 
  X, 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  MessageSquare, 
  Paperclip,
  Download,
  Mail
} from 'lucide-react';
import { Meeting, MeetingService } from '../../services/meeting';

interface MeetingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
}

const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({
  isOpen,
  onClose,
  meeting
}) => {
  const [fullMeeting, setFullMeeting] = useState<Meeting | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch full meeting details when modal opens
  useEffect(() => {
    if (isOpen && meeting) {
      console.log('MeetingDetailsModal opened with meeting:', meeting);
      console.log('Meeting attachments:', meeting.attachments);
      
      // If meeting already has attachments, use it directly
      if (meeting.attachments && Array.isArray(meeting.attachments) && meeting.attachments.length > 0) {
        console.log('Meeting already has attachments, using directly');
        setFullMeeting(meeting);
      } else {
        // Fetch full meeting details to get attachments
        console.log('Fetching full meeting details for ID:', meeting.id);
        fetchFullMeetingDetails(meeting.id);
      }
    } else {
      setFullMeeting(null);
    }
  }, [isOpen, meeting]);

  const fetchFullMeetingDetails = async (meetingId: number) => {
    console.log('fetchFullMeetingDetails called with ID:', meetingId);
    try {
      setIsLoading(true);
      const fullMeetingData = await MeetingService.getMeeting(meetingId);
      console.log('Fetched full meeting details:', fullMeetingData);
      console.log('Full meeting attachments:', fullMeetingData.attachments);
      setFullMeeting(fullMeetingData);
    } catch (error) {
      console.error('Error fetching full meeting details:', error);
      // Fallback to the original meeting data
      setFullMeeting(meeting);
    } finally {
      setIsLoading(false);
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string, duration: number) => {
    const [hours, minutes] = timeString.split(':');
    const startTime = new Date();
    startTime.setHours(parseInt(hours), parseInt(minutes));
    
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + duration);
    
    return `${startTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })} - ${endTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })}`;
  };

  const formatCreatedDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mock workshop data for display
  const workshopNames = {
    1: 'Lean Manufacturing Implementation',
    2: 'Quality Control Process',
    3: 'Safety Protocol Review',
    4: 'Equipment Maintenance',
    5: 'Team Training Program'
  };

  if (!isOpen) return null;

  // Use fullMeeting if available, otherwise fallback to meeting
  const displayMeeting = fullMeeting || meeting;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading meeting details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Meeting Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Meeting Title */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {displayMeeting.title}
            </h3>
            <p className="text-sm text-gray-500">
              Created by {displayMeeting.created_by} on {formatCreatedDate(displayMeeting.created_at)}
            </p>
          </div>

          {/* Meeting Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date & Time */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Date</h4>
                  <p className="text-gray-600">{formatDate(displayMeeting.date)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Time</h4>
                  <p className="text-gray-600">{formatTime(displayMeeting.time, displayMeeting.duration)}</p>
                  <p className="text-sm text-gray-500">Duration: {displayMeeting.duration} minutes</p>
                </div>
              </div>
            </div>

            {/* Location & Attendees */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Location</h4>
                  <p className="text-gray-600">{displayMeeting.location}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    Attendees ({displayMeeting.attendees.length})
                  </h4>
                  <div className="space-y-1">
                    {displayMeeting.attendees.map((attendee, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Mail size={14} className="text-gray-400" />
                        <span className="text-gray-600 text-sm">{attendee}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Workshops */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">
              Related Workshops ({displayMeeting.workshops_info?.length || 0})
            </h4>
            <div className="grid grid-cols-1 gap-2">
              {displayMeeting.workshops_info?.map((workshop) => (
                <div
                  key={workshop.id}
                  className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <div className="flex-1">
                    <span className="text-blue-900 font-medium">{workshop.title}</span>
                    <p className="text-blue-700 text-xs">{workshop.department_name}</p>
                  </div>
                </div>
              )) || <p className="text-gray-500 text-sm">No workshops selected</p>}
            </div>
          </div>

          {/* Message */}
          {displayMeeting.message && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">Meeting Message</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-gray-700 whitespace-pre-wrap">{displayMeeting.message}</p>
              </div>
            </div>
          )}

          {/* Attachments */}
          {displayMeeting.attachments && displayMeeting.attachments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Paperclip className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">
                  Attachments ({displayMeeting.attachments?.length || 0})
                </h4>
              </div>
              <div className="space-y-2">
                {displayMeeting.attachments?.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Paperclip size={16} className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.original_filename}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size_mb.toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          // Fetch the file as a blob to ensure proper download
                          const response = await fetch(attachment.file_url);
                          if (!response.ok) {
                            throw new Error('Failed to fetch file');
                          }
                          
                          const blob = await response.blob();
                          
                          // Create a blob URL and download
                          const blobUrl = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = attachment.original_filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          // Clean up the blob URL
                          window.URL.revokeObjectURL(blobUrl);
                        } catch (error) {
                          console.error('Download failed:', error);
                          // Fallback to opening in new tab if download fails
                          window.open(attachment.file_url, '_blank');
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                    >
                      <Download size={14} />
                      <span className="text-sm">Download</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meeting Status/Actions */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              <span className="text-green-800 font-medium">Meeting Scheduled</span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              Invitations have been sent to all attendees via email.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              // In a real app, this would resend invitations
              console.log('Resend invitations for meeting:', displayMeeting.id);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Mail size={16} />
            Resend Invitations
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailsModal;
